import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { authApi, useLoginByEmailPassword } from "@/api/authApi";

const OTPVerification = () => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const password = location.state?.password;
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes in seconds
  const loginMutation = useLoginByEmailPassword();

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    return Math.max(0, seconds);
  };

  const progressPercentage = (timeLeft / 120) * 100;

  const maskEmail = (email: string) => {
    if (!email) return "";
    const [username, domain] = email.split("@");
    // Always show first 3 chars + 3 asterisks, regardless of username length
    const maskedUsername = username.slice(0, 3) + "***";
    return `${maskedUsername}@${domain}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.querySelector(
          `input[name="otp-${index + 1}"]`
        );
        if (nextInput instanceof HTMLInputElement) {
          nextInput.focus();
        }
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").split("").slice(0, 6);

    if (digits.length) {
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const otpString = otp.join("");
      const response = await authApi.verifyOTP(email, otpString);
      console.log("response", response);
      console.log("response.data", response.data.access_token);
      if (response.status === 200) {
        Cookies.set("accessToken", response.data.access_token ?? "verified");
        localStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("refresh_token", response.data.refresh_token);
        navigate("/post-login");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleResendOTP = async () => {
    if (!email || !password) return;

    try {
      await loginMutation.mutateAsync({ email, password });
      // Reset timer to 2 minutes after successful resend
      setTimeLeft(120);
      // Clear OTP inputs
      setOtp(["", "", "", "", "", ""]);
    } catch (error) {
      console.error("Failed to resend OTP:", error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F5F6] p-4">
      <div className="w-full max-w-[600px] flex flex-col bg-white p-8 rounded-2xl">
        <h1 className="text-[#333333] text-2xl font-medium mb-6">
          Verify your identity
        </h1>
        <input
          disabled
          type="text"
          placeholder={`${email && maskEmail(email)}`}
          className="w-full p-2 rounded-lg border border-gray-300 "
        />

        <button
          onClick={() => navigate("/login")}
          className="text-green-500 hover:text-green-600 text-right"
        >
          Not you?
        </button>

        <p className="text-gray-500 mb-6">
          Since you're trying to perform a sensitive operation, we need
          re-verification to let you proceed.
        </p>

        <p className="text-gray-500 mb-6">
          Enter the OTP sent to {maskEmail(email)}
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800 text-sm">
            <strong>
              An One-Time Password (OTP) has been sent to your email:
              {maskEmail(email)}.
            </strong>{" "}
            If you didn't get the email, please check your spam. If there was no
            email there too, make sure you are using the correct
            already-registered email.
          </p>
        </div>

        <div className="flex mb-2 justify-around">
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              name={`otp-${index}`}
              maxLength={1}
              className="w-[50px] h-[65px] border border-gray-200 rounded-lg text-center text-xl focus:border-green-500 focus:ring-1 focus:ring-green-500 outline-none"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onPaste={(e) => handlePaste(e, index)}
            />
          ))}
        </div>

        <div className="relative mb-8">
          {timeLeft > 0 ? (
            <>
              <div className="absolute w-full h-[1px] bg-gray-200">
                <div
                  className="h-full bg-green-500 transition-all duration-1000"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="text-center text-[#98A2B3] text-sm relative top-[10px]">
                {formatTime(timeLeft)}
              </div>
            </>
          ) : (
            <div className="text-center">
              <button
                onClick={handleResendOTP}
                disabled={loginMutation.isPending}
                className="text-green-500 hover:text-green-600 disabled:text-gray-400 disabled:cursor-not-allowed text-sm font-medium"
              >
                {loginMutation.isPending ? "Sending..." : "Resend OTP"}
              </button>
            </div>
          )}
        </div>

        <button
          onClick={handleVerifyOtp}
          className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 max-w-[400px] mx-auto rounded-lg text-base font-normal"
        >
          verify OTP
        </button>
      </div>
    </div>
  );
};

export default OTPVerification;
